import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin, Sparkles, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useSeo } from "@/hooks/useSeo";

interface Destination {
  id: string; slug: string; title: string;
  short_description: string | null; long_description: string | null;
  main_image_url: string | null; gallery: any; features: any;
}
interface Tour {
  id: string; slug: string; title: string;
  main_image_url: string | null; duration: string | null; adult_price: number | null;
}

const arr = (v: any): string[] => (Array.isArray(v) ? v.filter(Boolean).map(String) : []);

const DestinationDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [destination, setDestination] = useState<Destination | null>(null);
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: destination ? `${destination.title} | Allen Travels RD` : "Destination | Allen Travels RD",
    description: destination?.short_description ?? undefined,
  });

  useEffect(() => {
    if (!slug) return;
    supabase.from("destinations").select("*").eq("slug", slug).eq("is_active", true).maybeSingle()
      .then(async ({ data }) => {
        const d = data as Destination | null;
        setDestination(d);
        if (d) {
          const { data: tData } = await supabase.from("tours")
            .select("id,slug,title,main_image_url,duration,adult_price")
            .eq("destination_id", d.id).eq("is_active", true).order("sort_order");
          setTours((tData as Tour[]) || []);
        }
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-32 container mx-auto px-4">
          <div className="h-[60vh] rounded-3xl bg-muted animate-pulse" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!destination) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center text-center px-4">
          <div>
            <h1 className="font-display text-3xl font-semibold">Destination not found</h1>
            <Button className="mt-6" onClick={() => navigate("/destinations")}>Back to destinations</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const gallery = arr(destination.gallery);
  const features = arr(destination.features);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <section className="relative min-h-[80vh] flex items-end pt-28">
        <div className="absolute inset-0 -z-10">
          {destination.main_image_url ? (
            <img src={destination.main_image_url} alt={destination.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-hero" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--hero-bg))]/60 via-[hsl(var(--hero-bg))]/40 to-[hsl(var(--hero-bg))]/95" />
        </div>
        <div className="container mx-auto px-4 pb-20 text-white">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="mt-6 max-w-3xl">
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-primary-glow font-semibold">
              <MapPin className="w-3.5 h-3.5" /> Dominican Republic
            </span>
            <h1 className="font-display text-5xl md:text-7xl font-semibold leading-[1.05] mt-3">{destination.title}</h1>
            {destination.short_description && (
              <p className="mt-5 text-lg text-white/85 max-w-2xl">{destination.short_description}</p>
            )}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-20 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 prose prose-neutral max-w-none">
          <p className="whitespace-pre-line text-foreground/85 leading-relaxed text-lg">
            {destination.long_description || destination.short_description}
          </p>
        </div>
        {features.length > 0 && (
          <div className="bg-gradient-card rounded-3xl border border-border/60 p-7 shadow-soft h-fit">
            <h3 className="font-display text-xl font-semibold mb-4">Highlights</h3>
            <ul className="space-y-3">
              {features.map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <Sparkles className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {gallery.length > 0 && (
        <section className="container mx-auto px-4 pb-20">
          <h2 className="font-display text-3xl font-semibold mb-6">Gallery</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {gallery.map((src, i) => (
              <div key={i} className="aspect-square rounded-2xl overflow-hidden bg-muted">
                <img src={src} alt="" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-smooth duration-700" />
              </div>
            ))}
          </div>
        </section>
      )}

      {tours.length > 0 && (
        <section className="container mx-auto px-4 pb-24">
          <h2 className="font-display text-3xl md:text-4xl font-semibold mb-8">Tours in {destination.title}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {tours.map((t) => (
              <Link key={t.id} to={`/tours/${t.slug}`} className="group relative overflow-hidden rounded-3xl bg-card shadow-card hover:shadow-elegant transition-smooth">
                <div className="aspect-[4/5] overflow-hidden bg-muted">
                  {t.main_image_url ? (
                    <img src={t.main_image_url} alt={t.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-smooth duration-700" />
                  ) : (
                    <div className="w-full h-full bg-gradient-ocean" />
                  )}
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--hero-bg))]/90 via-transparent to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                  <h3 className="font-display text-xl font-semibold">{t.title}</h3>
                  <div className="mt-1 flex items-center justify-between text-sm">
                    {t.duration && <span className="flex items-center gap-1 text-white/70"><Clock className="w-3.5 h-3.5" /> {t.duration}</span>}
                    {t.adult_price ? <span className="text-primary-glow">From ${t.adult_price}</span> : null}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="container mx-auto px-4 pb-24">
        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-ocean p-10 md:p-14 text-primary-foreground shadow-elegant">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="font-display text-3xl md:text-4xl font-semibold">Need a ride to {destination.title}?</h3>
              <p className="mt-3 text-white/85">Book a private transfer — we'll handle every kilometer.</p>
            </div>
            <div className="flex md:justify-end gap-3">
              <Link to="/transfers" className="px-7 py-3.5 rounded-full bg-white text-primary font-semibold shadow-glow hover:-translate-y-0.5 transition-smooth">
                Book a transfer <ArrowRight className="inline w-4 h-4 ml-1.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DestinationDetailPage;
